
import React, { useState, useEffect } from 'react';
import { CartItem, OptionValue, User } from '../types';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
  onCheckout: (method: string, pointsUsed?: number) => void;
  currentUser: User | null;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ 
  isOpen, onClose, items, onUpdateQuantity, onRemoveItem, onCheckout, currentUser 
}) => {
  const [pointInput, setPointInput] = useState<string>('0');
  const [pointsToUse, setPointsToUse] = useState<number>(0);

  // 상품 합계 금액 (수량 포함)
  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // 배송비 계산 로직: 10만원 이상 무료, 미만 시 담긴 상품 중 최저 배송비 적용
  const totalShipping = (items.length > 0 && totalAmount < 100000) 
    ? Math.min(...items.map(item => item.shippingFee)) 
    : 0;

  // 장바구니 금액이 5만원 미만으로 떨어지면 적용된 포인트 자동 취소
  useEffect(() => {
    if (totalAmount < 50000 && pointsToUse > 0) {
      setPointsToUse(0);
      setPointInput('0');
    }
  }, [totalAmount, pointsToUse]);

  const grandTotal = Math.max(0, totalAmount + totalShipping - pointsToUse);

  const handlePointApply = async () => {
    if (!currentUser) return;

    // 5만원 이상 구매 조건 체크
    if (totalAmount < 50000) {
      if (window.customAlert) await window.customAlert('포인트는 상품 합계 금액이 50,000원 이상일 때만 사용 가능합니다.');
      else alert('포인트는 상품 합계 금액이 50,000원 이상일 때만 사용 가능합니다.');
      setPointInput('0');
      setPointsToUse(0);
      return;
    }

    const val = Number(pointInput);
    if (val > currentUser.points) {
      if (window.customAlert) await window.customAlert('보유 포인트가 부족합니다.');
      else alert('보유 포인트가 부족합니다.');
      setPointInput('0');
      setPointsToUse(0);
    } else if (val > (totalAmount + totalShipping)) {
      if (window.customAlert) await window.customAlert('결제 금액을 초과할 수 없습니다.');
      else alert('결제 금액을 초과할 수 없습니다.');
      setPointInput('0');
      setPointsToUse(0);
    } else {
      setPointsToUse(val);
    }
  };

  const handleUseAllPoints = async () => {
    if (!currentUser) return;
    
    if (totalAmount < 50000) {
      if (window.customAlert) await window.customAlert('포인트는 상품 합계 금액이 50,000원 이상일 때만 사용 가능합니다.');
      else alert('포인트는 상품 합계 금액이 50,000원 이상일 때만 사용 가능합니다.');
      return;
    }

    setPointInput(String(currentUser.points));
    setPointsToUse(currentUser.points);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      {/* 배경 오버레이 */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-md" onClick={onClose} />
      
      {/* 장바구니 본체 - dvh 적용으로 모바일 높이 오류 해결 */}
      <div className="relative w-full max-w-lg bg-white h-[100dvh] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        
        {/* 헤더 영역 */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10 shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-black text-gray-900 italic tracking-tighter uppercase">Cart Storage</h2>
            <span className="px-2.5 py-1 bg-indigo-600 text-white text-xs font-black rounded-full shadow-md shadow-indigo-100">
              {items.length}
            </span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition group">
            <svg className="w-7 h-7 text-gray-400 group-hover:text-gray-900 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M6 18L18 6M6 6l12 12" strokeWidth={3}/>
            </svg>
          </button>
        </div>

        {/* 상품 목록 영역 (중간 스크롤 부분) */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50 scrollbar-hide">
          {items.map((item) => (
            <div key={item.cartId} className="flex flex-col gap-3 p-4 border border-gray-100 rounded-[28px] bg-white shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="flex gap-4">
                <div className="relative shrink-0">
                  <img src={item.image} className="w-20 h-20 rounded-[20px] object-cover border border-gray-100 shadow-sm" />
                  <button 
                    onClick={() => onRemoveItem(item.cartId)} 
                    className="absolute -top-1 -left-1 w-7 h-7 bg-white text-red-400 border border-red-50 rounded-full flex items-center justify-center shadow-lg hover:bg-red-500 hover:text-white transition-all scale-0 group-hover:scale-100"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="flex-1 flex flex-col min-w-0">
                  <h4 className="text-sm font-black text-gray-900 leading-tight truncate">{item.name}</h4>
                  
                  {/* 선택 옵션 표시 */}
                  {Object.entries(item.selectedOptions).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {Object.entries(item.selectedOptions).map(([key, val]) => (
                        <span key={key} className="text-[9px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md font-bold">
                          {(val as OptionValue).name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 추가 상품 표시 */}
                  {item.selectedAddOns.length > 0 && (
                    <div className="mt-1 space-y-1">
                      {item.selectedAddOns.map(addon => (
                        <div key={addon.id} className="text-[9px] text-indigo-400 font-bold flex items-center gap-1">
                          <span className="truncate flex-1">• {addon.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-auto pt-2 flex items-center justify-between">
                    <span className="text-sm font-black text-indigo-600 italic tracking-tight">{item.price.toLocaleString()}원</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-xl">
                  <button onClick={() => onUpdateQuantity(item.cartId, -1)} className="w-7 h-7 rounded-lg bg-white text-gray-400 font-black shadow-sm hover:text-indigo-600 transition">-</button>
                  <span className="w-6 text-center text-xs font-black">{item.quantity}</span>
                  {/* [Feature 4] + 버튼 색상 변경 (기존 회색 -> Hover시 인디고 유지, 기본 스타일 조정) */}
                  <button onClick={() => onUpdateQuantity(item.cartId, 1)} className="w-7 h-7 rounded-lg bg-white text-indigo-600 font-black shadow-sm hover:bg-indigo-600 hover:text-white transition">+</button>
                </div>
                <div className="text-right">
                  <span className="text-base font-black text-gray-900 italic tracking-tighter">{(item.price * item.quantity).toLocaleString()}원</span>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="py-32 text-center">
              <div className="w-20 h-20 bg-white rounded-[32px] flex items-center justify-center mx-auto mb-6 text-gray-200 shadow-sm border border-gray-50">
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" strokeWidth={2}/></svg>
              </div>
              <p className="text-gray-400 font-black italic text-lg uppercase tracking-tighter mb-2">Cart is Empty</p>
              <p className="text-gray-300 font-medium text-sm">상상하는 즐거움을 담아보세요!</p>
            </div>
          )}
        </div>

        {/* 결제 정보 및 구매하기 버튼 영역 (하단 고정) */}
        {items.length > 0 && (
          <div className="p-5 border-t border-gray-100 bg-white shadow-[0_-20px_50px_rgba(0,0,0,0.05)] space-y-4 shrink-0 pb-10 md:pb-5">
            {currentUser && (
              <div className="p-3 bg-indigo-50/40 rounded-2xl border border-indigo-100/30">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-1">
                    POINTS (보유: {currentUser.points.toLocaleString()}P)
                  </label>
                  <button 
                    onClick={handleUseAllPoints} 
                    className={`text-[9px] font-black underline ${totalAmount < 50000 ? 'text-gray-300 cursor-not-allowed' : 'text-indigo-600'}`}
                  >
                    전액 사용
                  </button>
                </div>
                <div className="flex gap-2">
                  <input 
                    type="number" 
                    value={pointInput} 
                    onChange={e => setPointInput(e.target.value)} 
                    className="flex-1 p-2 bg-white border border-indigo-100/20 rounded-xl text-sm font-black outline-none shadow-inner" 
                    placeholder="0"
                  />
                  <button 
                    onClick={handlePointApply} 
                    className={`px-4 py-2 rounded-xl text-[10px] font-black transition-all ${totalAmount < 50000 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white shadow-lg'}`}
                  >
                    적용
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-1.5 px-1">
              <div className="flex justify-between text-[11px] font-bold text-gray-400">
                <span>상품 금액</span>
                <span>{totalAmount.toLocaleString()}원</span>
              </div>
              <div className="flex justify-between text-[11px] font-bold text-gray-400">
                <span>배송비 {totalAmount >= 100000 && <span className="text-indigo-600 font-black ml-1">(10만↑ 무료)</span>}</span>
                <span className={totalShipping === 0 ? 'text-indigo-600' : ''}>{totalShipping === 0 ? '무료' : `${totalShipping.toLocaleString()}원`}</span>
              </div>
              {pointsToUse > 0 && (
                <div className="flex justify-between text-[11px] font-black text-indigo-600">
                  <span>포인트 사용</span>
                  <span>-{pointsToUse.toLocaleString()}원</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-dashed border-gray-100 mt-2">
                <span className="font-black text-gray-900 italic tracking-tighter uppercase text-xs">Total Payment</span>
                <span className="text-2xl font-black text-indigo-600 tracking-tighter italic">{grandTotal.toLocaleString()}원</span>
              </div>
            </div>

            <button 
              onClick={() => onCheckout('카드', pointsToUse)} 
              className="group relative w-full py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-xl active:scale-[0.98] flex items-center justify-center gap-2 overflow-hidden"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
              <span className="text-lg tracking-tighter uppercase italic">구매하기</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
